import { useState } from "react";

import type { KonfigurationsFeld } from "@repo/shared";

import { FeldRahmen } from "./FeldRahmen";
import { KonfiguratorSymbol } from "./KonfiguratorSymbol";

type AuswahlFeld = Extract<KonfigurationsFeld, { art: "auswahl" }>;

/**
 * Auswahl als Karten mit Bild (oben), Name und Hinweis. Ohne Bild – `bildUrl`
 * fehlt oder lädt nicht – steht das Symbol an dieser Stelle, sonst nichts.
 * Gesperrte Optionen bleiben sichtbar, sind aber nicht wählbar – der Grund
 * steht im Tooltip.
 */
export function AuswahlKarten({
  feld,
  wert,
  onChange,
  onGewaehlt,
  beschriftungVerbergen,
}: {
  feld: AuswahlFeld;
  wert: string;
  onChange: (wert: string) => void;
  /** Nach einer Wahl – der Wizard springt damit weiter. */
  onGewaehlt?: () => void;
  beschriftungVerbergen?: boolean;
}) {
  // URLs, die nicht geladen werden konnten – diese Karten zeigen wieder das Symbol.
  const [defekteBilder, setDefekteBilder] = useState<ReadonlySet<string>>(() => new Set());
  // Je URL nur einmal merken: Das Bild fällt danach weg (Symbol statt <img>), ein Fehler kann nicht schleifen.
  const merkeDefekt = (url: string) => setDefekteBilder((alt) => (alt.has(url) ? alt : new Set(alt).add(url)));

  return (
    <FeldRahmen feld={feld} beschriftungVerbergen={beschriftungVerbergen}>
      <div className="konfi-karten">
        {feld.optionen.map((option) => {
          const bildUrl = option.bildUrl && !defekteBilder.has(option.bildUrl) ? option.bildUrl : null;
          return (
            <button
              key={option.wert}
              type="button"
              className="konfi-karte"
              aria-pressed={wert === option.wert}
              disabled={feld.zustand.gesperrt || option.gesperrt}
              title={option.gesperrt && option.hinweis ? option.hinweis : undefined}
              onClick={() => {
                onChange(option.wert);
                onGewaehlt?.();
              }}
            >
              {bildUrl ? (
                <img
                  className="konfi-karte__bild"
                  src={bildUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={() => merkeDefekt(bildUrl)}
                />
              ) : (
                option.icon && (
                  <span className="konfi-karte__symbol">
                    <KonfiguratorSymbol name={option.icon} />
                  </span>
                )
              )}
              <span className="konfi-karte__titel">{option.label}</span>
              {option.hinweis && <span className="konfi-karte__hinweis">{option.hinweis}</span>}
            </button>
          );
        })}
      </div>
    </FeldRahmen>
  );
}
